import { formatMoney } from '@theme/money-formatting';

const STORAGE_KEY = 'houseOfSolidCampaignDiscount';
const CONFIG_ID = 'house-of-solid-campaign-discounts';

const parseConfig = () => {
  const configNode = document.getElementById(CONFIG_ID);
  if (!configNode) return null;

  try {
    const config = JSON.parse(configNode.textContent || '{}');
    const rulesText = String(config.rulesText || '').trim();
    const rules = rulesText ? JSON.parse(rulesText) : [];

    return {
      ...config,
      parameterNames: String(config.parameterNames || '')
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean),
      rules: Array.isArray(rules) ? rules : [],
    };
  } catch (error) {
    console.warn('Campaign discount rules could not be parsed.', error);
    return null;
  }
};

const normalizeCode = (value) => String(value || '').trim().toUpperCase();

const addCode = (codes, value) => {
  const code = normalizeCode(value);
  if (code && !codes.includes(code)) codes.push(code);
};

const getLooseUrlCodes = (parameterNames) => {
  const names = parameterNames.length ? parameterNames : ['code'];
  const rawUrl = window.location.href;
  const codes = [];

  const discountPathCode = rawUrl.match(/\/discount\/([^/?#]+)/i);
  if (discountPathCode?.[1]) addCode(codes, decodeURIComponent(discountPathCode[1].replace(/\+/g, ' ')));

  for (const name of names) {
    const pattern = new RegExp(`[?&#]${name}=([^?&#]+)`, 'gi');
    let match = pattern.exec(rawUrl);
    while (match) {
      addCode(codes, decodeURIComponent(match[1].replace(/\+/g, ' ')));
      match = pattern.exec(rawUrl);
    }
  }

  return codes;
};

const getUrlCodes = (parameterNames) => {
  const search = new URLSearchParams(window.location.search);
  const codes = [];

  for (const [key, value] of search.entries()) {
    const normalizedKey = key.toLowerCase();

    if (parameterNames.length === 0 || parameterNames.includes(normalizedKey)) {
      addCode(codes, value);
    }

    const keyAsCode = normalizeCode(key);
    if (keyAsCode && parameterNames.length === 0) addCode(codes, key);
  }

  getLooseUrlCodes(parameterNames).forEach((code) => addCode(codes, code));

  return codes;
};

const getActiveRule = (config) => {
  const urlCodes = getUrlCodes(config.parameterNames);

  for (const urlCode of urlCodes) {
    const matchedRule = config.rules.find((rule) => normalizeCode(rule.code) === urlCode);
    if (matchedRule) {
      window.sessionStorage.setItem(STORAGE_KEY, normalizeCode(matchedRule.code));
      return matchedRule;
    }
  }

  const storedCode = window.sessionStorage.getItem(STORAGE_KEY);
  if (!storedCode) return null;

  return config.rules.find((rule) => normalizeCode(rule.code) === normalizeCode(storedCode)) || null;
};

const syncCheckoutDiscount = (code, config) => {
  document.querySelectorAll('input[data-campaign-discount-input]').forEach((input) => input.remove());
  const normalizedCode = normalizeCode(code);
  if (!normalizedCode || !config.applyAtCheckout) return;

  document.querySelectorAll('form[action*="/cart"]').forEach((form) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'discount';
    input.value = normalizedCode;
    input.dataset.campaignDiscountInput = 'true';
    form.append(input);
  });
};

const formatPrice = (amount, config, useCurrency) => {
  const format = useCurrency ? config.moneyWithCurrencyFormat : config.moneyFormat;
  return formatMoney(amount, format || '{{amount}}', config.currency || 'INR');
};

const getOfferLabel = (rule, config) => {
  const numericValue = Number(rule.value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '';

  if (rule.type === 'flat') {
    return `${formatPrice(Math.round(numericValue * 100), config, false)} off`;
  }

  return `${numericValue}% off`;
};

const escapeHTML = (value) => {
  const div = document.createElement('div');
  div.textContent = String(value || '');
  return div.innerHTML;
};

const syncOfferMessaging = (rule, config) => {
  const offerLabel = rule ? getOfferLabel(rule, config) : '';
  const code = rule ? normalizeCode(rule.code) : '';

  document.querySelectorAll('[data-campaign-offer-note]').forEach((element) => {
    element.innerHTML =
      offerLabel && code
        ? `<strong>Get ${escapeHTML(offerLabel)}</strong><span>at checkout. Use</span><strong>${escapeHTML(code)}</strong>`
        : '';
    element.hidden = !(offerLabel && code);
  });

};

const getDefaultRule = (config) => {
  const code = normalizeCode(config.defaultCheckoutCode);
  if (!code) return null;

  return {
    code,
    type: config.defaultDiscountType === 'flat' ? 'flat' : 'percentage',
    value: config.defaultDiscountValue,
  };
};

const applyCampaignDiscounts = () => {
  const config = parseConfig();
  if (!config?.enabled) return;

  const campaignRule = getActiveRule(config);
  const rule = campaignRule || getDefaultRule(config);
  const checkoutCode = rule?.code;

  if (!rule) {
    syncCheckoutDiscount(checkoutCode, config);
    syncOfferMessaging(null, config);
    document.documentElement.removeAttribute('data-campaign-discount-code');
    return;
  }

  document.documentElement.dataset.campaignDiscountCode = normalizeCode(rule.code);
  syncCheckoutDiscount(checkoutCode, config);
  syncOfferMessaging(rule, config);
};

window.HouseOfSolidCampaignDiscounts = {
  apply: applyCampaignDiscounts,
};

let scheduled = false;
const scheduleApply = () => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    applyCampaignDiscounts();
  });
};

applyCampaignDiscounts();
document.addEventListener('shopify:section:load', applyCampaignDiscounts);
document.addEventListener('cart:update', () => requestAnimationFrame(applyCampaignDiscounts));

const observer = new MutationObserver((mutations) => {
  if (
    mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node instanceof HTMLElement &&
          (node.matches(
            '[data-campaign-offer-note], form[action*="/cart"]'
          ) ||
            node.querySelector(
              '[data-campaign-offer-note], form[action*="/cart"]'
            ))
      )
    )
  ) {
    scheduleApply();
  }
});

observer.observe(document.body, { childList: true, subtree: true });
